<template>
  <Head>

    <!-- Basic Meta Tags -->
    <title head-key="title">{{ title }}</title>
    <meta name="description" :content="description" head-key="description" />
    <meta name="keywords" :content="keywords" head-key="keywords" />

    <!-- Open Graph Meta Tags -->
    <meta property="og:title" :content="title" head-key="og:title" />
    <meta property="og:description" :content="description" head-key="og:description" />
    <meta property="og:type" content="website" head-key="og:type" />
    <meta property="og:url" :content="currentUrl" head-key="og:url" />
    <meta property="og:site_name" content="Powerprompt" head-key="og:site_name" />
    <meta v-if="ogImage" property="og:image" :content="ogImage" head-key="og:image" />
    <meta v-if="ogImageSecure" property="og:image:secure_url" :content="ogImageSecure" head-key="og:image:secure_url" />
    <meta v-if="ogImageWidth" property="og:image:width" :content="ogImageWidth" head-key="og:image:width" />
    <meta v-if="ogImageHeight" property="og:image:height" :content="ogImageHeight" head-key="og:image:height" />

    <!-- Twitter Card Meta Tags -->
    <meta name="twitter:card" :content="twitterCard" head-key="twitter:card" />
    <meta name="twitter:title" :content="title" head-key="twitter:title" />
    <meta name="twitter:description" :content="description" head-key="twitter:description" />
    <meta v-if="twitterImage" name="twitter:image" :content="twitterImage" head-key="twitter:image" />
    <meta v-if="twitterImageAlt" name="twitter:image:alt" :content="twitterImageAlt" head-key="twitter:image:alt" />
  </Head>
</template>

<script setup>
import { Head } from '@inertiajs/vue3';
import { computed, onMounted } from 'vue';
import { usePage } from '@inertiajs/vue3';

// Helper to interpolate placeholders in strings
function interpolate(str) {
    if (!str || typeof str !== 'string') return str;
    let result = str;
    const replacements = {
        category: (props.category && props.category.name) || '',
        subcategory: (props.subcategory && props.subcategory.name) || '',
        subsubcategory: (props.subsubcategory && props.subsubcategory.name) || '',
        tag: props.tag || '',
        title: (props.prompt && props.prompt.title) || ''
    };
    // Replace {key} with value
    Object.entries(replacements).forEach(([key, value]) => {
        if (value) {
            result = result.replaceAll(`{${key}}`, value);
        }
    });
    return result;
}

const props = defineProps({
    // Direct meta object from the server
    meta: {
        type: Object,
        required: false,
        default: () => ({})
    },
    // Category data for category pages
    category: {
        type: Object,
        required: false,
        default: null
    },
    // Subcategory data for subcategory pages
    subcategory: {
        type: Object,
        required: false,
        default: null
    },
    // Subsubcategory data for subsubcategory pages
    subsubcategory: {
        type: Object,
        required: false,
        default: null
    },
    // Tag data for tag pages
    tag: {
        type: String,
        required: false,
        default: null
    },
    // Prompt data for prompt pages
    prompt: {
        type: Object,
        required: false,
        default: null
    },
    // Page type to determine which meta tags to generate
    pageType: {
        type: String,
        required: false,
        default: null
    }
});

const page = usePage();

// Cleanup lingering debug meta if backend says debug is disabled
onMounted(() => {
    if (typeof document === 'undefined') return;
    const debugEnabled = !!(page?.props?.debugEnabled);
    try {
        const metas = Array.from(document.head.querySelectorAll('meta'))
            .map(m => ({ name: m.getAttribute('name'), headKey: m.getAttribute('head-key'), inertia: m.getAttribute('inertia') }))
            .filter(m => (m.name === 'debug-info') || (m.headKey === 'debug-info'));
        // console.debug('[GlobalMetaTags] onMounted meta debug entries:', metas, 'debugEnabled:', debugEnabled);
    } catch (_) {}
    if (!debugEnabled) {
        const tags = document.head?.querySelectorAll('meta[name="debug-info"]') || [];
        tags.forEach(tag => tag.parentNode && tag.parentNode.removeChild(tag));
    }
});

// Get the current URL for og:url
const currentUrl = computed(() => {
    // Prefer backend-provided canonical/url, fallback to window location
    if (props.meta && (props.meta.canonical || props.meta.url)) {
        return props.meta.canonical || props.meta.url;
    }
    if (typeof window !== 'undefined') {
        return window.location.href;
    }
    return '';
});

// Helper function to generate fallback title
function generateFallbackTitle() {
    if (props.pageType === 'category' && props.category) {
        return `${props.category.name} Prompts`;
    } else if (props.pageType === 'subcategory' && props.category && props.subcategory) {
        return `${props.subcategory.name} Prompts in ${props.category.name}`;
    } else if (props.pageType === 'subsubcategory' && props.subsubcategory) {
        return `${props.subsubcategory.name} Prompts`;
    } else if (props.pageType === 'prompt' && props.prompt) {
        return props.prompt.title || 'Prompt Details';
    } else if (props.pageType === 'tag' && props.tag) {
        return `${props.tag} Prompts`;
    }

    // Fallback
    return 'Powerprompt - AI Prompt Library';
}

// Helper function to generate fallback description
function generateFallbackDescription() {
    if (props.pageType === 'category' && props.category) {
        return `Explore ${props.category.name} AI prompts for ChatGPT, Claude, and other AI assistants.`;
    } else if (props.pageType === 'subcategory' && props.category && props.subcategory) {
        return `Discover ${props.subcategory.name} prompts in the ${props.category.name} category for AI assistants.`;
    } else if (props.pageType === 'subsubcategory' && props.subsubcategory) {
        return `Find the best ${props.subsubcategory.name} for your AI assistant.`;
    } else if (props.pageType === 'prompt' && props.prompt) {
        return props.prompt.description || 'Detailed AI prompt to enhance your conversations with AI assistants.';
    } else if (props.pageType === 'tag' && props.tag) {
        return `Collection of AI prompts tagged with ${props.tag}.`;
    }

    // Fallback
    return 'Discover and use the best AI prompts for ChatGPT, Claude, and other AI assistants.';
}

// Use server-provided meta if available, otherwise generate based on props
const title = computed(() => {
    // First priority: server-provided meta
    if (props.meta && props.meta.title) {
        // Direct return of server-provided title (no translation key processing needed)
        return interpolate(props.meta.title);
    }

    // Second priority: generate based on page type and props
    return generateFallbackTitle();
});

// Generate description based on available data
const description = computed(() => {
    // First priority: server-provided meta
    if (props.meta && props.meta.description) {
        // Direct return of server-provided description (no translation key processing needed)
        return interpolate(props.meta.description);
    }

    // Second priority: generate based on page type and props
    return generateFallbackDescription();
});

// Get OG image from meta if available
const ogImage = computed(() => props.meta?.ogImage || null);
const ogImageSecure = computed(() => props.meta?.ogImageSecure || null);
const ogImageWidth = computed(() => props.meta?.ogImageWidth || null);
const ogImageHeight = computed(() => props.meta?.ogImageHeight || null);

// Get Twitter image from meta if available
const twitterImage = computed(() => props.meta?.twitterImage || null);
const twitterImageAlt = computed(() => props.meta?.twitterImageAlt || null);

// Get Twitter card type from meta (default to summary_large_image if image available, otherwise summary)
const twitterCard = computed(() => {
    return props.meta?.twitterCard || (twitterImage.value ? 'summary_large_image' : 'summary');
});

// Generate keywords based on available data
const keywords = computed(() => {
    // First priority: server-provided meta
    if (props.meta && props.meta.keywords) {
        return interpolate(props.meta.keywords);
    }

    // Second priority: generate based on page type and props
    let keywordsList = [];

    if (props.pageType === 'category' && props.category) {
        keywordsList.push(props.category.name);
    } else if (props.pageType === 'subcategory' && props.category && props.subcategory) {
        keywordsList.push(props.category.name, props.subcategory.name);
    } else if (props.pageType === 'subsubcategory' && props.category && props.subcategory && props.subsubcategory) {
        keywordsList.push(props.category.name, props.subcategory.name, props.subsubcategory.name);
    } else if (props.pageType === 'prompt' && props.prompt && props.prompt.title) {
        keywordsList.push(props.prompt.title);
    } else if (props.pageType === 'tag' && props.tag) {
        keywordsList.push(props.tag);
    }

    // Return comma-separated keywords
    return keywordsList.join(', ');
});
</script>
